import { useState } from "react";
import { EllipsisVertical } from "lucide-react";
import { useApiUserApplicationsCommentsDestroy, useApiUserApplicationsCommentsUpdate } from "@/api/lctApIComponents";
import { UserApplicationComment } from "@/api/lctApISchemas";
import { AlertDialog } from "@/components/AlertDialog";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { useAppSelector } from "@/hooks/reduxHooks";
import { useDates } from "@/hooks/useDates";
import { scrollToElement } from "@/utils/helpers";
import logo from "@/assets/LCT_CircleIcon.png";

interface IProps {
  comment: UserApplicationComment;
  sectionTitle?: string;
  userAppId: string;
  refetch?: () => void;
  editable?: boolean;
}

export const FeedbackItem: React.FC<IProps> = ({ comment, sectionTitle, userAppId, refetch, editable = false }) => {
  const token = useAppSelector((state) => state.user.authKey);
  const { formatDateToShortDateTime } = useDates();
  const deleteComment = useApiUserApplicationsCommentsDestroy();
  const editComment = useApiUserApplicationsCommentsUpdate();
  const [editCommentText, setEditCommentText] = useState(comment.comment_text);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const onEditComment = () => {
    setIsEditing(true);
    setError(null);
  };

  const onSaveEdit = () => {
    const trimmedText = editCommentText.trim();
    if (!trimmedText) {
      setError("Comment cannot be empty");
      return;
    }

    editComment.mutate(
      {
        headers: { Authorization: `Token ${token}` },
        pathParams: { id: comment.id, userapp: userAppId },
        body: { comment_text: trimmedText },
      },
      {
        onSuccess: () => {
          setIsEditing(false);
          setError(null);
          if (refetch) {
            refetch();
          }
        },
      },
    );
  };

  const onCancelEdit = () => {
    setEditCommentText(comment.comment_text);
    setIsEditing(false);
    setError(null);
  };

  const onDeleteComment = () => {
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    deleteComment.mutate(
      {
        headers: { Authorization: `Token ${token}` },
        pathParams: { id: comment.id, userapp: userAppId },
      },
      {
        onSuccess: () => {
          if (refetch) {
            refetch();
          }
        },
      },
    );
  };

  return (
    <>
      <section
        key={comment.id}
        className={`rounded-lg bg-white p-4 transition-colors ${comment.associated_section ? "cursor-pointer hover:bg-gray-50" : ""}`}
        onClick={comment.associated_section ? () => scrollToElement(comment.associated_section) : undefined}
      >
        <header className="flex items-center justify-between">
          <h3 className="text-xs font-semibold text-gray-600">{sectionTitle ? sectionTitle : "General"}</h3>
          {editable ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-6 w-6 border-0">
                  <EllipsisVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" side="left" sideOffset={0}>
                <DropdownMenuGroup>
                  <DropdownMenuItem onClick={onDeleteComment}>Delete</DropdownMenuItem>
                  <DropdownMenuItem onClick={onEditComment}>Edit</DropdownMenuItem>
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </header>
        <div className="flex flex-row items-center">
          <div className="relative mr-2 flex h-8 w-8 items-center justify-center">
            <div className="absolute inset-0 rounded-full bg-[#ECFCCB]"></div>
            <img className="relative h-6 w-6" src={logo} alt="logo" />
          </div>
          <div className="my-1 flex flex-col">
            <span className="text-sm font-semibold text-gray-900">{comment.user_full_name}</span>
            {comment.edited ? <span className="text-xs text-gray-500">{`Edited by ${comment.edited_by_name}`}</span> : null}
            <span className="text-xs text-gray-500">{formatDateToShortDateTime(comment.updated_at)}</span>
          </div>
        </div>
        {isEditing ? (
          <div className="mt-2 space-y-2">
            <Textarea
              value={editCommentText}
              onChange={(e) => {
                setEditCommentText(e.target.value);
                setError(null);
              }}
              className={`min-h-[100px] ${error ? "border-red-500" : ""}`}
              placeholder="Enter your comment..."
            />
            {error && <p className="text-sm text-red-500">{error}</p>}
            <div className="flex justify-end space-x-2">
              <Button variant="outline" size="sm" onClick={onCancelEdit}>
                Cancel
              </Button>
              <Button size="sm" onClick={onSaveEdit} disabled={!editCommentText.trim()}>
                Save
              </Button>
            </div>
          </div>
        ) : (
          <span className="text-sm font-normal text-gray-700">{editCommentText}</span>
        )}
      </section>

      <AlertDialog
        showDialog={showDeleteDialog}
        setShowDialog={setShowDeleteDialog}
        onConfirm={handleDeleteConfirm}
        title="Delete Comment"
        description="Are you sure you want to delete this comment? This action cannot be undone."
        actionText="Delete"
        cancelText="Cancel"
      />
    </>
  );
};
